import { useRef, useState } from "react";
import {
  Download,
  Upload,
  X,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import {
  downloadCSVTemplate,
  parseCSV,
  transformCSVToProjectData,
  type ParsedProjectData,
} from "../lib/csvImport";
import { TemplateGuideModal } from "./TemplateGuideModal";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";

import { useProjectLocations } from "../hooks/useDashpivot";
import { useExistingProjectNames } from "../hooks/useProjects";
import { ProjectConfirmationModal } from "./ProjectConfirmationModal";
import { logError } from "../lib/logError";

interface ImportProjectsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ImportProjectsModal({
  isOpen,
  onClose,
  onSuccess,
}: ImportProjectsModalProps) {
  const { user } = useAuth();

  // Dashpivot Projects for validation/guide
  const { names: existingNames } = useExistingProjectNames();
  const { locations, loading: loadingLocations } =
    useProjectLocations(existingNames);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ParsedProjectData[]>([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successCount, setSuccessCount] = useState(0);
  const [templateDownloaded, setTemplateDownloaded] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const parseAndPreview = async (file: File) => {
    try {
      const text = await file.text();
      // Filter out comments (lines starting with #) before parsing
      const csvContent = text
        .split("\n")
        .filter((line) => !line.trim().startsWith("#"))
        .join("\n");

      // csvImport.ts parseCSV returns CSVProjectRow[], which we need to group by project
      // But looking at csvImport codebase, it seems designed to parse ONE project per file or multpile?
      // Wait, transformCSVToProjectData takes rows and returns ParsedProjectData (singular object).
      // Ah, the transform function seems to assume the CSV contains data for A SINGLE project (grouped by categories).
      // Let's re-read csvImport.ts.
      // "Transform CSV rows into project data structure... returns ParsedProjectData"
      // "rows: CSVProjectRow[]"
      // "const firstRow = rows[0];" -> "job_location: firstRow.job_location"
      // So yes, the current logic assumes one project per CSV file.
      // That's fine for now, or we can iterate if we want to support multiple projects.
      // But for simplicity, let's assume 1 project per file as per the current lib design.

      const rows = parseCSV(csvContent);
      const projectData = transformCSVToProjectData(rows);
      setPreviewData([projectData]); // Array of 1 for now
      setError(null);

      // Automatically open confirmation modal
      setIsConfirmationOpen(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    } catch (err: any) {
      setError(err.message || "Failed to parse CSV");
      setPreviewData([]);
    }
  };

  const handleFile = (selectedFile: File) => {
    if (
      selectedFile.type !== "text/csv" &&
      !selectedFile.name.endsWith(".csv")
    ) {
      setError("Please upload a CSV file");
      return;
    }
    setFile(selectedFile);
    parseAndPreview(selectedFile);
  };

  const handleImport = async () => {
    if (!previewData.length || !user) return;
    setImporting(true);
    setError(null);
    setSuccessCount(0);

    try {
      const orgId = requireOrganizationId();

      for (const projectData of previewData) {
        // 1. Create Project
        const { data: project, error: projError } = await supabase
          .from("claims_projects")
          .insert({
            organization_id: orgId,
            job_location: projectData.job_location,
            job_address: projectData.job_address,
            project_details: projectData.project_details,
            normal_rate: projectData.normal_rate,
            overtime_rate: projectData.overtime_rate,
            saturday_rate: projectData.saturday_rate || 400,
            saturday_contract_ot_rate: projectData.saturday_contract_ot_rate || null,
            status: projectData.status,
            builder_name: projectData.builder_name,
            builder_address: projectData.builder_address,
            builder_poc_name: projectData.builder_poc_name,
            builder_email: projectData.builder_email,
            created_by: user.id,
          })
          .select()
          .single();

        if (projError) throw projError;

        // 2. Create Categories & Subcategories
        for (const [catIndex, cat] of projectData.categories.entries()) {
          const { data: category, error: catError } = await supabase
            .from("claims_categories")
            .insert({
              organization_id: orgId,
              project_id: project.id,
              name: cat.name,
              sort_order: catIndex,
              is_default: false,
              is_custom_amount_only: cat.isFixedAmount,
              custom_amount: cat.isFixedAmount ? cat.fixedAmount : null,
            })
            .select()
            .single();

          if (catError) throw catError;

          if (!cat.isFixedAmount && cat.subcategories.length > 0) {
            const subcategoriesToInsert = cat.subcategories.map(
              (sub, subIndex) => ({
                organization_id: orgId,
                category_id: category.id,
                name: sub.name,
                sort_order: subIndex,
                amount: sub.amount,
                is_default: false,
              }),
            );

            const { error: subError } = await supabase
              .from("claims_subcategories")
              .insert(subcategoriesToInsert);

            if (subError) throw subError;
          } else if (cat.isFixedAmount) {
            // Create FIXED AMOUNT subcategory for fixed amount categories
            const { error: subError } = await supabase
              .from("claims_subcategories")
              .insert({
                organization_id: orgId,
                category_id: category.id,
                name: "FIXED AMOUNT",
                sort_order: 0,
                amount: cat.fixedAmount || 0,
                is_default: false,
              });

            if (subError) throw subError;
          }
        }
        setSuccessCount((prev) => prev + 1);
      }

      setTimeout(() => {
        onSuccess();
        onClose();
        // Reset state
        setFile(null);
        setPreviewData([]);
        setSuccessCount(0);
      }, 1000);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    } catch (err: any) {
      logError("Import failed:", err);
      setError(err.message || "Failed to create project");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: "100%",
          maxWidth: "500px",
          margin: "var(--spacing-md)",
          padding: 0,
          overflow: "hidden",
          animation: "slideIn 0.2s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "var(--spacing-lg)",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "var(--bg-secondary)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-sm)",
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "var(--border-radius-md)",
                background: "rgba(59, 130, 246, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--color-primary)",
              }}
            >
              <FileSpreadsheet size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.125rem", fontWeight: 600 }}>
                Import Project
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.875rem",
                  color: "var(--text-muted)",
                }}
              >
                Create a new project from CSV
              </p>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: "var(--spacing-xl)" }}>
          {/* Step 1: Download Template */}
          <div style={{ marginBottom: "var(--spacing-xl)" }}>
            <h4
              style={{
                fontSize: "0.875rem",
                fontWeight: 600,
                marginBottom: "var(--spacing-md)",
                color: "var(--text-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Step 1: Get the Template</span>
              {templateDownloaded && (
                <span
                  style={{
                    color: "var(--color-success)",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    fontSize: "0.75rem",
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  <CheckCircle2 size={14} /> Downloaded
                </span>
              )}
            </h4>

            {!templateDownloaded ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "var(--spacing-md)",
                  padding: "var(--spacing-md)",
                  background: "var(--bg-secondary)",
                  borderRadius: "var(--border-radius-md)",
                  border: "1px solid var(--border-color)",
                }}
              >
                <FileSpreadsheet
                  size={20}
                  style={{ color: "var(--color-primary)", marginTop: 2 }}
                />
                <div style={{ flex: 1 }}>
                  <p style={{ margin: "0 0 4px", fontWeight: 500 }}>
                    Download CSV Template
                  </p>
                  <p
                    style={{
                      margin: "0 0 12px",
                      fontSize: "0.875rem",
                      color: "var(--text-secondary)",
                    }}
                  >
                    Use this template to ensure your project data is formatted
                    correctly.
                  </p>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setShowGuide(true);
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Download size={14} />
                    Download Template
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "var(--spacing-sm) var(--spacing-md)",
                  background: "rgba(16, 185, 129, 0.1)",
                  borderRadius: "var(--border-radius-md)",
                  border: "1px solid rgba(16, 185, 129, 0.2)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-sm)",
                  }}
                >
                  <CheckCircle2
                    size={16}
                    style={{ color: "var(--color-success)" }}
                  />
                  <span
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--color-success)",
                      fontWeight: 500,
                    }}
                  >
                    Template downloaded
                  </span>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowGuide(true)}
                  title="Download again"
                  style={{ padding: 4, height: 24, width: 24 }}
                >
                  <Download size={14} style={{ opacity: 0.6 }} />
                </button>
              </div>
            )}
          </div>

          {/* Step 2: Upload */}
          <div>
            <h4
              style={{
                fontSize: "0.875rem",
                fontWeight: 600,
                marginBottom: "var(--spacing-md)",
                color: "var(--text-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Step 2: Upload CSV
            </h4>

            {!file ? (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${
                    dragActive ? "var(--color-primary)" : "var(--border-color)"
                  }`,
                  borderRadius: "var(--border-radius-lg)",
                  padding: "var(--spacing-xl)",
                  textAlign: "center",
                  background: dragActive
                    ? "rgba(59, 130, 246, 0.05)"
                    : "var(--bg-card)",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: "none" }}
                  onChange={(e) =>
                    e.target.files?.[0] && handleFile(e.target.files[0])
                  }
                />
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    background: "var(--bg-secondary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto var(--spacing-md)",
                    color: "var(--text-muted)",
                  }}
                >
                  <Upload size={24} />
                </div>
                <p style={{ margin: "0 0 4px", fontWeight: 500 }}>
                  Click to upload or drag and drop
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.875rem",
                    color: "var(--text-muted)",
                  }}
                >
                  CSV files only (max 5MB)
                </p>
              </div>
            ) : (
              <div
                style={{
                  border: "1px solid var(--border-color)",
                  borderRadius: "var(--border-radius-lg)",
                  padding: "var(--spacing-md)",
                  background: "var(--bg-card)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--spacing-md)",
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "var(--border-radius-md)",
                        background: "rgba(16, 185, 129, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--color-success)",
                      }}
                    >
                      <FileSpreadsheet size={20} />
                    </div>
                    <div>
                      <p
                        style={{
                          margin: "0 0 2px",
                          fontWeight: 500,
                          fontSize: "0.9rem",
                        }}
                      >
                        {file.name}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.75rem",
                          color: "var(--text-muted)",
                        }}
                      >
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  {!importing && !successCount && (
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        setPreviewData([]);
                        setError(null);
                      }}
                      style={{ color: "var(--text-muted)" }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {error && (
                  <div
                    style={{
                      marginTop: "var(--spacing-md)",
                      padding: "var(--spacing-sm)",
                      background: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid rgba(239, 68, 68, 0.2)",
                      borderRadius: "var(--border-radius-sm)",
                      color: "var(--color-error)",
                      fontSize: "0.875rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <AlertCircle size={14} />
                    {error}
                  </div>
                )}

                {previewData.length > 0 && !importing && !successCount && (
                  <div style={{ marginTop: "var(--spacing-md)" }}>
                    {/* Confirmation Modal */}
                    <ProjectConfirmationModal
                      isOpen={isConfirmationOpen}
                      importedProjectName={previewData[0].job_location}
                      availableProjects={locations}
                      existingProjectNames={existingNames}
                      projectData={previewData[0]}
                      onConfirm={(confirmedName) => {
                        // Update the project name with the confirmed one
                        const updatedData = [...previewData];
                        updatedData[0].job_location = confirmedName;
                        setPreviewData(updatedData);
                        setIsConfirmationOpen(false);
                        handleImport();
                      }}
                      onCancel={() => {
                        setIsConfirmationOpen(false);
                        setFile(null);
                        setPreviewData([]);
                      }}
                    />

                    {/* Preview of what will be imported (simplified) */}
                    <div
                      style={{
                        padding: "var(--spacing-md)",
                        background: "var(--bg-secondary)",
                        borderRadius: "var(--border-radius-md)",
                        marginBottom: "var(--spacing-md)",
                        border: "1px solid var(--border-color)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <p
                            style={{
                              margin: "0 0 4px",
                              fontWeight: 600,
                              fontSize: "0.9rem",
                            }}
                          >
                            Project Data Found
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "0.8rem",
                              color: "var(--text-secondary)",
                            }}
                          >
                            {previewData[0].job_location} •{" "}
                            {previewData[0].categories.length} categories
                          </p>
                        </div>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => setIsConfirmationOpen(true)}
                        >
                          Review & Import
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {importing && (
                  <div
                    style={{
                      marginTop: "var(--spacing-md)",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        color: "var(--color-primary)",
                      }}
                    >
                      <Loader2 size={16} className="spin" />
                      <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                        Creating project structure...
                      </span>
                    </div>
                  </div>
                )}

                {successCount > 0 && (
                  <div
                    style={{
                      marginTop: "var(--spacing-md)",
                      padding: "var(--spacing-md)",
                      background: "rgba(16, 185, 129, 0.1)",
                      border: "1px solid rgba(16, 185, 129, 0.2)",
                      borderRadius: "var(--border-radius-md)",
                      textAlign: "center",
                      color: "var(--color-success)",
                    }}
                  >
                    <CheckCircle2 size={24} style={{ marginBottom: 8 }} />
                    <p style={{ margin: 0, fontWeight: 600 }}>
                      Project Imported!
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <TemplateGuideModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        availableProjects={locations}
        isLoadingProjects={loadingLocations}
        onDownload={() => {
          downloadCSVTemplate(locations);
          setTemplateDownloaded(true);
        }}
        type="project"
      />
    </div>
  );
}
